import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	Button,
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";

import DropdownController from "../../components/inputs/DropdownController";
import DatePickerController from "../../components/inputs/DatePickerController";
import TextFieldController from "../../components/inputs/TextFieldController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import {
	calculateDownTime,
	getCodeDescription,
	maxLengthMessage,
} from "../../utils/functions";

// Validation Schema - Fixed for Yup v1.6.1
const workOrderSchema = yup.object().shape({
	vehicleId: yup
		.number()
		.nullable()
		.required("Vehicle is required")
		.transform((v, o) => (o === "" ? null : v)),
	type: yup.string().nullable().required("Type is required"),
	serviceRequirementId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	status: yup.string().required("Status is required"),
	perPersonnelId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	vendor: yup.string().trim().max(255, maxLengthMessage(255)).nullable(),
	cost: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	odometerAtService: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	openedDate: yup.string().required("Opened date is required"),
	closedDate: yup
		.string()
		.nullable()
		.when("status", {
			is: "Closed",
			then: (schema) =>
				schema.required(
					"Closed date is required when status is Closed",
				),
			otherwise: (schema) => schema.nullable(),
		}),
	notes: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
	fundingSourceId: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	newVehicleStatus: yup.string().nullable(),
});

const buildDefaults = (initialData) => ({
	vehicleId: initialData?.vehicleId || "",
	type: initialData?.type || initialData?.typeInfo?.Code || "",
	serviceRequirementId: initialData?.serviceRequirementId || "",
	status: initialData?.status || initialData?.statusInfo?.Code || "",
	perPersonnelId: initialData?.perPersonnelId
		? String(initialData?.perPersonnelId)
		: "",
	vendor: initialData?.vendor || "",
	cost: initialData?.cost || "",
	odometerAtService: initialData?.odometerAtService || "",
	openedDate: initialData?.openedDate
		? moment(initialData.openedDate).format("MM/DD/YYYY")
		: moment().format("MM/DD/YYYY"),
	closedDate: initialData?.closedDate
		? moment(initialData.closedDate).format("MM/DD/YYYY")
		: "",
	notes: initialData?.notes || "",
	fundingSourceId: initialData?.fundingSourceId || "",
	newVehicleStatus: "",
});

const MileageWarn = ({ entered, current }) => {
	if (!entered || !current) return null;

	const enteredNum = parseFloat(entered);
	const currentNum = parseFloat(current);

	let type = null;
	if (enteredNum < currentNum) type = "low";
	else if (enteredNum > currentNum + 1000) type = "high";

	if (!type) return null;

	const isLow = type === "low";
	return (
		<Box
			sx={{
				mt: 1,
				p: "7px 10px",
				borderRadius: 1,
				background: isLow
					? "rgba(239,68,68,0.08)"
					: "rgba(245,166,35,0.10)",
				border: `1px solid ${isLow ? "rgba(239,68,68,0.35)" : "rgba(245,166,35,0.4)"}`,
				fontSize: 11,
				fontWeight: 600,
				color: isLow ? "#ef4444" : "#f59e0b",
				display: "flex",
				alignItems: "center",
				gap: 1,
			}}
		>
			⚠️
			{isLow
				? `Below current odometer (${(+current).toLocaleString()} mi) — verify reading`
				: `${(enteredNum - currentNum).toLocaleString()} mi above current odometer — verify reading`}
		</Box>
	);
};

const AddEditWorkOrderDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	viewOnly = false,
	vehicleOptions = [],
	workOrderTypes = [],
	statusOptions = [],
	employeeOptions = [],
	fundingSourceOptions = [],
	vehicleStatusOptions = [],
	serviceRequirements = [],
}) => {
	const user = useSelector((state) => state.user);
	const dictionary = useSelector((state) => state.dictionary.dictionary);
	const isEdit = !!initialData;

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors, isValid },
	} = useForm({
		resolver: yupResolver(workOrderSchema),
		defaultValues: buildDefaults(initialData),
		mode: "onChange",
	});

	// Reset form when dialog opens with new data
	useEffect(() => {
		if (open) {
			reset(buildDefaults(initialData));
		}
	}, [open, initialData, reset]);

	// Reset on close
	useEffect(() => {
		if (!open) reset();
	}, [open, reset]);

	// Watched values
	const vehicleId = watch("vehicleId");
	const status = watch("status");
	const odometerAtService = watch("odometerAtService");

	const selectedVehicle = useMemo(
		() => vehicleOptions.find((v) => v.value === Number(vehicleId)),
		[vehicleOptions, vehicleId],
	);

	const vehicleOdometer = selectedVehicle?.odometer;

	// Get service requirements for selected vehicle
	const serviceReqOptions = useMemo(() => {
		if (!serviceRequirements) return [];
		// const vehicleReqs = selectedVehicle.serviceRequirements || [];
		return serviceRequirements.map((req) => ({
			value: req.ptsCodeId,
			label: `${req.Code} — every ${Number(req.Value).toLocaleString()} ${req.Unit}`,
		}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedVehicle, serviceRequirements]);

	const onSubmit = (data) => {
		const payload = {
			vehicleId: data.vehicleId ? Number(data.vehicleId) : null,
			type: data.type || null,
			serviceRequirementId: data.serviceRequirementId
				? Number(data.serviceRequirementId)
				: null,
			status: data.status,
			perPersonnelId: data.perPersonnelId
				? Number(data.perPersonnelId)
				: null,
			vendor: data.vendor?.trim() || null,
			cost: data.cost || null,
			odometerAtService: data.odometerAtService || null,
			openedDate: data.openedDate
				? moment(data.openedDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			closedDate: data.closedDate
				? moment(data.closedDate, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			notes: data.notes?.trim() || null,
			fundingSourceId: data.fundingSourceId
				? Number(data.fundingSourceId)
				: null,
			newVehicleStatus: data.newVehicleStatus || null,
			agencyId: user?.userAgency || null,
		};

		onSave(payload);
	};

	const printWOList = () => {
		const _d = (v) => (v != null && v !== "" ? String(v) : "\u2014");
		const _rows = [initialData].map(function (wo) {
			var veh = vehicleOptions.find(function (v) {
				return v.id === wo.vehicleId;
			});
			var emp = (employeeOptions || []).find(function (e) {
				return Number(e.value) === wo.perPersonnelId;
			});

			const endDate = wo?.closedDate || new Date();
			var dt = calculateDownTime(wo?.openedDate, endDate);

			return [
				"WO-" + String(wo.id).slice(-4).padStart(4, "0"),
				veh
					? veh.unitNumber +
						" \u2014 " +
						veh.year +
						" " +
						veh.make +
						" " +
						veh.model
					: "\u2014",
				_d(wo.type),
				wo.status,
				emp ? emp.label : "\u2014",
				_d(wo.vendor),
				_d(wo.openedDate),
				wo.closedDate || "Open",
				dt,
				wo.cost != null
					? "$" + Number(wo.cost).toLocaleString()
					: "\u2014",
			];
		});
		const _tbody = _rows
			.map(function (r) {
				return (
					"<tr>" +
					r
						.map(function (c) {
							return "<td>" + c + "</td>";
						})
						.join("") +
					"</tr>"
				);
			})
			.join("");
		const _css =
			"* {box-sizing:border-box;margin:0;padding:0} body {font-family:Arial,Helvetica,sans-serif;font-size:10px;color:#1a1a1a;padding:20px} h1 {font-size:16px;font-weight:800;margin-bottom:2px} .meta {font-size:9px;color:#888;margin-bottom:16px} table {width:100%;border-collapse:collapse} thead tr {background:#1a1a1a;color:#fff} th {text-align:left;padding:5px 7px;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.06em} td {padding:4px 7px;border-bottom:1px solid #eee;vertical-align:top} tr:nth-child(even) td {background:#fafafa} .footer {margin-top:16px;padding-top:8px;border-top:1px solid #ddd;font-size:9px;color:#aaa} @media print {body{padding:10px} @page{margin:12mm 10mm 20mm} thead tr{-webkit-print-color-adjust:exact;print-color-adjust:exact} tr:nth-child(even) td{-webkit-print-color-adjust:exact;print-color-adjust:exact} .footer{position:fixed;bottom:0;left:0;right:0;border-top:1px solid #ddd;padding:4px 12px;font-size:9px;color:#999;background:#fff;display:flex;justify-content:space-between}}";
		const _extra =
			"Total: $" +
			[initialData]
				.reduce(function (s, w) {
					return s + (w.cost || 0);
				}, 0)
				.toLocaleString();
		const _html =
			'<!DOCTYPE html><html><head><meta charset="utf-8"><title>Work Orders</title><style>' +
			_css +
			'</style></head><body><h1>Work Orders</h1><div class="meta">' +
			_rows.length +
			" records" +
			(_extra ? " \u00b7 " + _extra : "") +
			" \u00b7 Printed " +
			new Date().toLocaleString() +
			" \u00b7 Printed by " +
			("printedBy" || "Unknown") +
			"</div><table><thead><tr><th>WO #</th><th>Vehicle</th><th>Type</th><th>Status</th><th>Assigned To</th><th>Vendor</th><th>Opened</th><th>Closed</th><th>Downtime</th><th>Cost</th></tr></thead><tbody>" +
			_tbody +
			'</tbody></table><div class="footer"><div style="display:flex;justify-content:space-between"><span>Asset Management \u00b7 Work Orders</span><span>Printed ' +
			new Date().toLocaleString() +
			" \u00b7 Printed by " +
			("printedBy" || "Unknown") +
			"</span></div></div><scr" +
			"ipt>window.onload=function(){window.print();};</scr" +
			"ipt></body></html>";
		const _w = window.open("", "_blank", "width=1100,height=700");
		if (_w) {
			_w.document.write(_html);
			_w.document.close();
		}
	};

	const dialogTitle = isEdit
		? viewOnly
			? `View Work Order — WO-${String(initialData.id).slice(-4).padStart(4, "0")}`
			: `Edit Work Order — WO-${String(initialData.id).slice(-4).padStart(4, "0")}`
		: "New Work Order";

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent
				sx={{
					pointerEvents: viewOnly ? "none" : undefined,
					opacity: viewOnly ? 0.78 : undefined,
					pt: 2,
					marginTop: "15px",
				}}
			>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
					id="WorkOrderForm"
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(2, 1fr)",
						gap: 2,
					}}
				>
					{/* Vehicle */}
					<Box sx={{ gridColumn: "1 / -1" }}>
						<DropdownController
							name="vehicleId"
							label="Vehicle *"
							options={vehicleOptions.map((v) => ({
								value: v.value,
								label: v.label,
							}))}
							control={control}
							errors={errors}
							disabled={isEdit}
						/>
						{isEdit && (
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.5, display: "block" }}
							>
								Vehicle cannot be changed after creation
							</Typography>
						)}
					</Box>

					{/* Type */}
					<DropdownController
						name="type"
						label="Type *"
						options={workOrderTypes}
						control={control}
						errors={errors}
					/>

					{/* Fulfills Service Requirement */}
					<DropdownController
						name="serviceRequirementId"
						label="Fulfills Service Requirement"
						options={serviceReqOptions}
						control={control}
						errors={errors}
						placeholder="None / N/A"
					/>

					{/* Status */}
					<DropdownController
						name="status"
						label="Status *"
						options={statusOptions}
						control={control}
						errors={errors}
					/>

					{/* Assigned To */}
					<DropdownController
						name="perPersonnelId"
						label="Assigned To (optional)"
						options={employeeOptions}
						control={control}
						errors={errors}
						placeholder="— Unassigned —"
					/>

					{/* Vendor */}
					<TextFieldController
						name="vendor"
						label="Vendor"
						control={control}
						errors={errors}
						placeholder="Repair shop / vendor"
					/>

					{/* Cost */}
					<TextFieldController
						name="cost"
						label="Cost ($)"
						control={control}
						errors={errors}
						type="number"
						placeholder="0.00"
					/>

					{/* Odometer */}
					<Box>
						<TextFieldController
							name="odometerAtService"
							label="Odometer at Service (mi)"
							control={control}
							errors={errors}
							type="number"
							placeholder={
								vehicleOdometer
									? String(vehicleOdometer)
									: "Optional"
							}
						/>
						{vehicleOdometer && odometerAtService && (
							<MileageWarn
								entered={odometerAtService}
								current={vehicleOdometer}
							/>
						)}
					</Box>

					{/* Opened Date */}
					<DatePickerController
						name="openedDate"
						label="Opened Date *"
						control={control}
						errors={errors}
					/>

					{/* Closed Date (only when status=Closed) */}
					{status === "Closed" && (
						<DatePickerController
							name="closedDate"
							label="Closed Date *"
							control={control}
							errors={errors}
						/>
					)}

					{/* Notes */}
					<Box sx={{ gridColumn: "1 / -1" }}>
						<TextFieldController
							name="notes"
							label="Notes"
							control={control}
							errors={errors}
							multiline
							rows={3}
							placeholder="Describe the work required..."
						/>
					</Box>

					{/* Funding Source */}
					<DropdownController
						name="fundingSourceId"
						label="Funding Source (optional)"
						options={fundingSourceOptions.map((fs) => ({
							value: fs.value,
							label: fs.label,
						}))}
						control={control}
						errors={errors}
						placeholder="— Not linked to funding source —"
					/>

					{/* Vehicle Status (only when vehicle selected) */}
					{selectedVehicle && (
						<Box
							sx={{
								gridColumn: "1 / -1",
								pt: 2,
								mt: 1,
								borderTop: "1px solid",
								borderColor: "divider",
							}}
						>
							<Typography
								variant="caption"
								fontWeight={700}
								color="text.secondary"
								sx={{
									textTransform: "uppercase",
									letterSpacing: "0.06em",
									mb: 1,
									display: "block",
								}}
							>
								Vehicle Status
							</Typography>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									flexWrap: "wrap",
									gap: 2,
								}}
							>
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Current:{" "}
									<strong
										style={{
											color:
												selectedVehicle.status ===
												"Out of Service"
													? "#ef4444"
													: selectedVehicle.status ===
														  "Maintenance"
														? "#f59e0b"
														: "#22c55e",
											marginLeft: "0.5ch",
										}}
									>
										{getCodeDescription(
											selectedVehicle.status,
											dictionary.codePerVehicleStatus,
										) || "Unknown"}
									</strong>
								</Typography>
								<Box sx={{ minWidth: 200 }}>
									<DropdownController
										name="newVehicleStatus"
										label="Set New Status"
										options={vehicleStatusOptions}
										control={control}
										errors={errors}
										size="small"
									/>
								</Box>
							</Box>
						</Box>
					)}
				</Box>
			</DialogContent>

			<DialogActions
				sx={{ px: 3, pb: 2, justifyContent: "space-between" }}
			>
				<Box>
					{isEdit && (
						<Button
							variant="outlined"
							onClick={printWOList}
							title="Print work order"
						>
							🖨️ Print
						</Button>
					)}
				</Box>
				<Box sx={{ display: "flex", gap: 1 }}>
					<PATCloseButton onClick={onClose} tooltipText="Cancel" />
					{!viewOnly && (
						<PATSaveButton
							onClick={handleSubmit(onSubmit)}
							tooltipText={
								isEdit ? "Save Changes" : "Create Work Order"
							}
							disabled={!isValid}
						/>
					)}
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default AddEditWorkOrderDialog;
